// Importación masiva: lo que el front le pregunta y le pide al backend. El front nunca lee las
// tablas: todo pasa por /v1/importaciones/* (ADR-2026-09-29-importacion-masiva-front).

import { atlasBackendFetch, atlasBackendGet } from "./atlasBackend";
import {
  ErrorDeMercadoLibre,
  mensajeDeErrorDeMercadoLibre,
} from "./mercadoLibreConexionLogica";
import type {
  EstadoDeImportaciones,
  VistaImportacion,
} from "./importacionesLogica";

export * from "./importacionesLogica";

/** De dónde se importa (ADR-2026-09-30-refresh-busca-en-todo-lo-conectado). */
export type OrigenDeImportacion = VistaImportacion["origen"];

async function leerJson(r: Response): Promise<Record<string, unknown> | null> {
  try {
    return (await r.json()) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** La importación en curso y la última de la marca. null = no se pudo preguntar (se reintenta). */
export async function estadoDeImportacion(
  brandId: string | null,
  origen: OrigenDeImportacion = "mercadolibre",
): Promise<EstadoDeImportaciones | null> {
  try {
    const q = new URLSearchParams({ origen });
    if (brandId) q.set("brand_id", brandId);
    const r = await atlasBackendGet(`/v1/importaciones/estado?${q}`);
    if (!r.ok) return null;
    const b = await leerJson(r);
    return {
      activa: (b?.activa as VistaImportacion) ?? null,
      ultima: (b?.ultima as VistaImportacion) ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * ¿El backend deja importar desde este origen? Un backend sin la fase 2 del 5/10 contesta 404 (o 403) a quien no es
 * dev tester: ahí no se muestra el botón (fallaría al tocarlo). Sin importaciones también es true. Un corte de red o
 * un error del servidor NO lo esconde (review Greptile #160): si no, el botón desaparecía hasta recargar.
 */
export async function origenDisponible(brandId: string | null, origen: OrigenDeImportacion): Promise<boolean> {
  try {
    const q = new URLSearchParams({ origen });
    if (brandId) q.set("brand_id", brandId);
    const r = await atlasBackendGet(`/v1/importaciones/estado?${q}`);
    return r.status !== 403 && r.status !== 404;
  } catch {
    return true;
  }
}

async function post(
  ruta: string,
  brandId: string | null,
  /** Sólo las rutas de una importación (cancelar, buscar); las de repetidos no lo usan. */
  origen?: OrigenDeImportacion,
): Promise<Record<string, unknown> | null> {
  let r: Response;
  try {
    r = await atlasBackendFetch(ruta, {
      brand_id: brandId,
      ...(origen ? { origen } : {}),
    });
  } catch {
    throw new ErrorDeMercadoLibre("red");
  }
  const b = await leerJson(r);
  if (!r.ok) {
    const code = typeof b?.error === "string" ? b.error : "error";
    throw new Error(
      typeof b?.message === "string"
        ? b.message
        : mensajeDeErrorDeMercadoLibre(code),
    );
  }
  return b;
}

export async function cancelarImportacion(
  brandId: string | null,
  origen: OrigenDeImportacion = "mercadolibre",
): Promise<void> {
  await post("/v1/importaciones/cancelar", brandId, origen);
}

/** "Buscar productos nuevos": trae lo que falta (lo que ya está no se duplica). */
export async function buscarProductosNuevos(
  brandId: string | null,
  origen: OrigenDeImportacion = "mercadolibre",
): Promise<VistaImportacion | null> {
  const b = await post("/v1/importaciones/buscar-nuevos", brandId, origen);
  return (b?.importacion as VistaImportacion) ?? null;
}

// ── "¿Es el mismo producto? ¿Los unimos?" (ADR-2026-09-29-importacion-masiva-duplicados) ──

export interface ProductoDeSugerencia {
  id: string;
  nombre: string | null;
  foto: string | null;
  /** De dónde vino (null = cargado en Atlas). */
  origen?: OrigenDeImportacion | null;
}

export interface SugerenciaDeRepetido {
  id: string;
  motivo: string | null;
  /** El que llegó de la importación (Mercado Libre o Tiendanube). */
  nuevo: ProductoDeSugerencia;
  /** El que la marca ya tenía en Atlas. */
  existente: ProductoDeSugerencia;
}

/** Las pendientes de la marca. [] si no se pudo preguntar (no es algo que haya que cantar). */
export async function sugerenciasDeRepetidos(
  brandId: string | null,
): Promise<SugerenciaDeRepetido[]> {
  try {
    const q = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : "";
    const r = await atlasBackendGet(`/v1/importaciones/sugerencias${q}`);
    if (!r.ok) return [];
    const b = await leerJson(r);
    return Array.isArray(b?.sugerencias)
      ? (b.sugerencias as SugerenciaDeRepetido[])
      : [];
  } catch {
    return [];
  }
}

/** Unir: la publicación pasa al producto que ya estaba. `borrado`: se borró el importado (nadie lo usaba). */
export async function unirRepetido(
  id: string,
  brandId: string | null,
): Promise<{ borrado: boolean }> {
  const b = await post(
    `/v1/importaciones/sugerencias/${encodeURIComponent(id)}/unir`,
    brandId,
  );
  return { borrado: b?.borrado === true };
}

export async function descartarRepetido(
  id: string,
  brandId: string | null,
): Promise<void> {
  await post(
    `/v1/importaciones/sugerencias/${encodeURIComponent(id)}/descartar`,
    brandId,
  );
}

/**
 * Análisis a pedido (ADR-2026-10-05-analisis-a-pedido): la persona eligió productos importados que esperaban su
 * análisis. Se le pide al backend que los analice ya, en silencio, para que cuando genere estén listos. Si no
 * llega, no pasa nada: el backend igual los analiza al generar.
 */
export async function pedirAnalisisAPedido(varianteIds: string[]): Promise<string[]> {
  // De a 20 (el tope de la ruta), todas las tandas. Devuelve las que el backend aceptó: las otras se pueden volver
  // a pedir (review Greptile #160). Nunca tira: generar lo cubre.
  const aceptadas: string[] = [];
  for (let i = 0; i < varianteIds.length; i += 20) {
    const tanda = varianteIds.slice(i, i + 20);
    try {
      const r = await atlasBackendFetch("/v1/importaciones/analizar", { variante_ids: tanda });
      if (r.ok) aceptadas.push(...tanda);
    } catch {
      /* silencioso */
    }
  }
  return aceptadas;
}

/** Los que hay que pedir: esperan su análisis y todavía no se pidieron en esta pantalla. Puro. */
export function idsParaAnalizar(productos: Array<{ id: string; analisisPendiente?: boolean }>, yaPedidos: Set<string>): string[] {
  return [...new Set(productos.filter((p) => p.analisisPendiente && !yaPedidos.has(p.id)).map((p) => p.id))];
}
